// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { useIsExists } from "@west/App/Hooks/useIsExists";
import { CustomAppState } from "@west/App/Store/Reducers";
import { XNGAGE_CUSTOM_WIDGET } from "@west/XDL/constants";
import translate from "@west/XDL/PreStyle/translate";
import { xParserOptions } from "@west/XDL/Utilities/BasicSelectors";
import parse from "html-react-parser";
import * as React from "react";

const enum fields {
    title = "title",
    description = "description",
    buttonLabel = "buttonLabel",
    id = "id",
    containerId = "containerId",
}

interface ProductDetailsSimilarProductsProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.description]: string;
        [fields.buttonLabel]: string;
        [fields.id]: string;
        [fields.containerId]: string;
    };
}

interface ProductDetailsSimilarProductsStyles {
    container?: InjectableCss;
    title?: TypographyProps;
    description?: TypographyProps;
    link?: LinkPresentationProps;
}

export const productDetailsSimilarProductsStyles: ProductDetailsSimilarProductsStyles = {};

const styles = productDetailsSimilarProductsStyles;
const ProductDetailsSimilarProducts: React.FC<ProductDetailsSimilarProductsProps> = ({ fields }) => {
    const isCarouselExists = useIsExists(fields.id, fields.containerId || "ProductDetailsViewExtraSection");
    const { headerHeight } = useAppSelector(state => ({
        headerHeight: parseInt((state as CustomAppState).xHeader.headerHeight, 10),
    }));

    const handleLinkClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        event.preventDefault();
        const carouselElement = document.getElementById(fields.id);
        const windowIfDefined = typeof window === "undefined" ? null : window;

        if (!carouselElement || !windowIfDefined) {
            return;
        }

        const offsetPosition = carouselElement.offsetTop - headerHeight;
        window.scrollTo({
            top: offsetPosition,
            behavior: "smooth",
        });
    };

    if (!isCarouselExists) {
        return null;
    }

    return (
        <StyledWrapper {...styles.container} data-test-selector="similar-products">
            <Typography {...styles.title}>{fields.title}</Typography>
            <Typography {...styles.description}>{parse(fields.description, xParserOptions())}</Typography>
            <Link href={`#${fields.id}`} aria-label={fields.buttonLabel} onClick={handleLinkClick} {...styles.link}>
                {fields.buttonLabel}
            </Link>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: ProductDetailsSimilarProducts,
    definition: {
        group: XNGAGE_CUSTOM_WIDGET as any,
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                isRequired: true,
                fieldType: "General",
            },
            {
                name: fields.description,
                displayName: "Description",
                editorTemplate: "RichTextField",
                defaultValue: "",
                isRequired: false,
                fieldType: "General",
            },
            {
                name: fields.buttonLabel,
                editorTemplate: "TextField",
                displayName: "Button Label",
                defaultValue: "",
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.id,
                editorTemplate: "TextField",
                displayName: "Carousel Id",
                defaultValue: "",
                fieldType: "General",
                isRequired: true,
                tooltip: "Id of the carousel to be used. Provide the Id of the specific carousel",
            },
            {
                name: fields.containerId,
                editorTemplate: "TextField",
                displayName: "Carousel Container Id",
                defaultValue: "ProductDetailsViewExtraSection",
                fieldType: "General",
                isRequired: false,
                tooltip: "Id of the container where the carousel will be displayed.",
            },
        ],
    },
};

export default widgetModule;
